import React, { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import TentativeFamilyPlanList from "./Phase-04/TentativeFamilyPlan/TentativeFamilyPlanList";
import { selectHasPermission } from "../store/slices/authSlice";
import {
    fetchFamilyPlans,
    deleteFamilyPlan,
    clearError,
} from "../store/slices/tentativeFamilyPlanSlice";

const TentativeFamilyPlans = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // ✅ Permissions (replace later with real roles)
    const canManagePlans = useSelector((state) =>
        selectHasPermission(state, "family-plan-manage")
    );
    const canCreatePlans = useSelector((state) =>
        selectHasPermission(state, "family-plan-create")
    );
    const canEditPlans = useSelector((state) =>
        selectHasPermission(state, "family-plan-edit")
    );
    const canDeletePlans = useSelector((state) =>
        selectHasPermission(state, "family-plan-delete")
    );

    // ✅ Redux state
    const { familyPlans, isLoading, error } = useSelector(
        (state) => state.tentativeFamilyPlan
    );

    // ✅ Pagination state (local)
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [currentPage, setCurrentPage] = React.useState(1);

    // ✅ Fetch data on mount or pagination change
    useEffect(() => {
        dispatch(fetchFamilyPlans());
    }, [dispatch]);

    // ✅ Handlers
    const handleAddPlan = () => navigate("/phase04/tentative-family-plan/add");
    const handleEditPlan = (id) => navigate(`/phase04/tentative-family-plan/edit/${id}`);

    const handleDeletePlan = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This record will be deleted permanently!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            dispatch(deleteFamilyPlan(id))
                .unwrap()
                .then(() => {
                    Swal.fire("Deleted!", "Tentative family plan deleted.", "success");
                })
                .catch(() => {
                    Swal.fire("Error!", "Failed to delete record.", "error");
                });
        }
    };

    // ✅ Permission restriction
    if (!canManagePlans) {
        return (
            <Navigate
                to="/dashboard"
                state={{ error: "You do not have permission to manage tentative family plans." }}
                replace
            />
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <strong>Error:</strong> {error}
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={() => dispatch(clearError())}
                        ></button>
                    </div>
                )}

                {/* ===== Tentative Family Plan Table ===== */}
                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <TentativeFamilyPlanList
                                plans={familyPlans}
                                onEditPlan={canEditPlans ? handleEditPlan : null}
                                onDeletePlan={canDeletePlans ? handleDeletePlan : null}
                                onAddPlan={canCreatePlans ? handleAddPlan : null}
                                totalRows={familyPlans?.length || 0}
                                rowsPerPage={rowsPerPage}
                                currentPage={currentPage}
                                setRowsPerPage={setRowsPerPage}
                                setCurrentPage={setCurrentPage}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default TentativeFamilyPlans;
